import React, { useState, useEffect } from 'react';
import { adminApi } from '../services/api';
import '../assets/ProfitSharerAdjustmentAlert.css';

const MONTH_NAMES = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const ProfitSharerAdjustmentAlert = () => {
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [notes, setNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');

  useEffect(() => {
    fetchPendingAdjustments();
    const interval = setInterval(fetchPendingAdjustments, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchPendingAdjustments = async () => {
    try {
      const response = await adminApi.getPendingProfitSharerAdjustments();
      if (response.data.success) {
        setPendingAdjustments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending adjustments:', error);
    }
  };

  const openModal = (index = 0) => {
    setCurrentIndex(index);
    setNotes('');
    setActionError('');
    setActionSuccess('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setActionError('');
    setActionSuccess('');
  };

  const selectedAdjustment = pendingAdjustments[currentIndex];

  const handleApprove = async () => {
    if (!selectedAdjustment) return;
    setActionError('');
    setActionSuccess('');
    try {
      setActionLoading(true);
      const response = await adminApi.approveProfitSharerAdjustment(selectedAdjustment._id, notes);
      if (response.data.success) {
        setActionSuccess('Adjustment approved. A payment adjustment has been queued for next month.');
        await fetchPendingAdjustments();
        // Move to next if available, else close
        if (pendingAdjustments.length <= 1) {
          setTimeout(closeModal, 1500);
        } else {
          setCurrentIndex(Math.max(0, currentIndex - 1));
          setNotes('');
        }
      }
    } catch (error) {
      setActionError(error.response?.data?.message || 'Failed to approve adjustment');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAdjustment) return;
    if (!notes.trim()) {
      setActionError('A reason is required to reject this adjustment.');
      return;
    }
    setActionError('');
    setActionSuccess('');
    try {
      setActionLoading(true);
      const response = await adminApi.rejectProfitSharerAdjustment(selectedAdjustment._id, notes);
      if (response.data.success) {
        setActionSuccess('Adjustment rejected.');
        await fetchPendingAdjustments();
        if (pendingAdjustments.length <= 1) {
          setTimeout(closeModal, 1500);
        } else {
          setCurrentIndex(Math.max(0, currentIndex - 1));
          setNotes('');
        }
      }
    } catch (error) {
      setActionError(error.response?.data?.message || 'Failed to reject adjustment');
    } finally {
      setActionLoading(false);
    }
  };

  if (pendingAdjustments.length === 0) return null;

  const diff = selectedAdjustment?.difference ?? 0;
  const isPositive = diff > 0;

  return (
    <>
      {/* Floating banner */}
      <div className="psa-banner">
        <div className="psa-banner-icon">⚠</div>
        <div className="psa-banner-text">
          <strong>Profit Sharer Adjustment Required</strong>
          <span>
            {pendingAdjustments.length} pending adjustment{pendingAdjustments.length > 1 ? 's' : ''} — backdated lesson{pendingAdjustments.length > 1 ? 's' : ''} affect already-paid commissions
          </span>
        </div>
        <button className="psa-banner-btn" onClick={() => openModal(0)}>
          Review ({pendingAdjustments.length})
        </button>
      </div>

      {/* Modal */}
      {showModal && selectedAdjustment && (
        <div className="psa-overlay" onClick={closeModal}>
          <div className="psa-modal" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="psa-modal-header">
              <div className="psa-modal-title">
                <span className="psa-modal-icon">⚠</span>
                <div>
                  <h3>Profit Sharer Adjustment Review</h3>
                  <p>A backdated lesson has changed an already-paid commission</p>
                </div>
              </div>
              <button className="psa-close-btn" onClick={closeModal} aria-label="Close">×</button>
            </div>

            {/* Pagination if multiple */}
            {pendingAdjustments.length > 1 && (
              <div className="psa-pagination">
                <button
                  className="psa-page-btn"
                  onClick={() => { setCurrentIndex(i => Math.max(0, i - 1)); setNotes(''); setActionError(''); setActionSuccess(''); }}
                  disabled={currentIndex === 0}
                >‹ Prev</button>
                <span>{currentIndex + 1} of {pendingAdjustments.length}</span>
                <button
                  className="psa-page-btn"
                  onClick={() => { setCurrentIndex(i => Math.min(pendingAdjustments.length - 1, i + 1)); setNotes(''); setActionError(''); setActionSuccess(''); }}
                  disabled={currentIndex === pendingAdjustments.length - 1}
                >Next ›</button>
              </div>
            )}

            {/* Body */}
            <div className="psa-modal-body">

              {/* Summary cards */}
              <div className="psa-summary-grid">
                <div className="psa-card">
                  <span className="psa-card-label">Profit Sharer</span>
                  <span className="psa-card-value">{selectedAdjustment.profitSharer?.name}</span>
                </div>
                <div className="psa-card">
                  <span className="psa-card-label">Affected Period</span>
                  <span className="psa-card-value">
                    {MONTH_NAMES[selectedAdjustment.affectedMonth]} {selectedAdjustment.affectedYear}
                  </span>
                </div>
                <div className="psa-card">
                  <span className="psa-card-label">Original Commission</span>
                  <span className="psa-card-value">${selectedAdjustment.originalCommission?.toFixed(2)}</span>
                </div>
                <div className="psa-card">
                  <span className="psa-card-label">Revised Commission</span>
                  <span className="psa-card-value">${selectedAdjustment.newCommission?.toFixed(2)}</span>
                </div>
              </div>

              {/* Difference highlight */}
              <div className={`psa-diff-row ${isPositive ? 'psa-diff-positive' : 'psa-diff-negative'}`}>
                <span>Commission difference to carry forward</span>
                <span className="psa-diff-amount">
                  {isPositive ? '+' : ''}${diff.toFixed(2)}
                </span>
              </div>

              {/* Reason */}
              <div className="psa-reason">
                <span className="psa-reason-label">Reason:</span>
                <span>{selectedAdjustment.reason}</span>
              </div>

              {/* Backdated lessons */}
              {selectedAdjustment.lessons && selectedAdjustment.lessons.length > 0 && (() => {
                // Derive allocation % from the known totals
                const totalPoolIncrease = selectedAdjustment.lessons.reduce((s, l) => s + (l.commission || 0), 0);
                const allocPct = totalPoolIncrease > 0
                  ? Math.round((diff / totalPoolIncrease) * 100)
                  : null;
                return (
                  <div className="psa-lessons">
                    <h4>
                      Backdated Lesson{selectedAdjustment.lessons.length > 1 ? 's' : ''} Added After Payment
                      <span className="psa-lessons-count">{selectedAdjustment.lessons.length}</span>
                    </h4>
                    <p className="psa-lessons-note">
                      Pool commission per lesson × {allocPct != null ? `${allocPct}% allocation` : 'allocation %'} = personal share.
                      Total pool increase: <strong>${totalPoolIncrease.toFixed(2)}</strong> → {selectedAdjustment.profitSharer?.name?.split(' ')[0]}'s share: <strong>{isPositive ? '+' : ''}${diff.toFixed(2)}</strong>
                    </p>
                    <div className="psa-lessons-header">
                      <span>Date</span>
                      <span>Tutee</span>
                      <span>Tutor</span>
                      <span>Pool Commission</span>
                    </div>
                    <div className="psa-lessons-list">
                      {selectedAdjustment.lessons.map((lesson, i) => (
                        <div key={i} className="psa-lesson-row">
                          <span>{new Date(lesson.date).toLocaleDateString('en-SG', { timeZone: 'Asia/Singapore', day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          <span>{lesson.tutee}</span>
                          <span>{lesson.tutor}</span>
                          <span className="psa-lesson-commission">${lesson.commission?.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Notes */}
              <div className="psa-notes">
                <label htmlFor="psa-notes-input">
                  Admin Notes
                  <span className="psa-notes-hint">(optional for approval, required for rejection)</span>
                </label>
                <textarea
                  id="psa-notes-input"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add context or reason for your decision..."
                  rows={3}
                />
              </div>

              {/* Feedback */}
              {actionError && <div className="psa-feedback psa-feedback-error">{actionError}</div>}
              {actionSuccess && <div className="psa-feedback psa-feedback-success">{actionSuccess}</div>}
            </div>

            {/* Footer */}
            <div className="psa-modal-footer">
              <button className="psa-btn psa-btn-ghost" onClick={closeModal} disabled={actionLoading}>
                Cancel
              </button>
              <button className="psa-btn psa-btn-reject" onClick={handleReject} disabled={actionLoading}>
                {actionLoading ? 'Processing...' : 'Reject'}
              </button>
              <button className="psa-btn psa-btn-approve" onClick={handleApprove} disabled={actionLoading}>
                {actionLoading ? 'Processing...' : 'Approve & Carry to Next Month'}
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default ProfitSharerAdjustmentAlert;
